import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { SKIP_LOADING } from '@fuse/services/loading/loading.interceptor';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';

export type ImportJobStatus = 'QUEUED' | 'PROCESSING' | 'DONE' | 'ERROR';

export interface ImportJob {
  jobId: string;
  status: ImportJobStatus;
  fileNames: string[];
  certId?: number;
  errorMessage?: string;
  stageMessage?: string;
  stageName?: string;
  targetCertId?: number;
  createdAt: string;
  elapsedMs?: number;
  _startedAt?: number;
}

const BASE = `${environment.apiUrl}/tradex/tradex-import`;
const FIRST_POLL_MS = 1_000;
const FAST_POLL_MS = 2_000;
const SLOW_POLL_MS = 10_000;
const SLOW_AFTER_MS = 2 * 60 * 1000;
const JOB_EXPIRY_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY = (email: string) => `tradex-import-jobs::${email}`;

@Injectable({ providedIn: 'root' })
export class TradexImportService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  readonly jobs = signal<ImportJob[]>([]);
  readonly jobCompleted$ = new Subject<ImportJob>();
  readonly openCertRequested$ = new Subject<number>();

  requestOpenCert(certId: number): void {
    this.openCertRequested$.next(certId);
  }

  readonly activeCount = computed(() =>
    this.jobs().filter(j => j.status === 'QUEUED' || j.status === 'PROCESSING').length
  );
  readonly hasActive = computed(() => this.activeCount() > 0);
  readonly completedCount = computed(() =>
    this.jobs().filter(j => j.status === 'DONE' || j.status === 'ERROR').length
  );

  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor() {
    this.requestNotificationPermission();
    this.restore();
    effect(() => {
      const list = this.jobs();
      const key = STORAGE_KEY(this.userEmail());
      try {
        const serializable = list.map(({ _startedAt, elapsedMs, ...rest }) => rest);
        localStorage.setItem(key, JSON.stringify(serializable));
      } catch { /* quota exceeded */ }
    });
  }

  uploadFiles(files: File[]): void {
    const form = new FormData();
    files.forEach(f => form.append('files', f, f.name));
    this.http.post<ImportJob>(`${BASE}/upload`, form).subscribe({
      next: (job) => this.registerJob(job),
      error: (err) => console.error('[TradexImport] upload failed', err),
    });
  }

  appendFiles(certId: number, files: File[]): void {
    const form = new FormData();
    files.forEach(f => form.append('files', f, f.name));
    this.http.post<ImportJob>(`${BASE}/append/${certId}`, form).subscribe({
      next: (job) => this.registerJob(job),
      error: (err) => console.error('[TradexImport] append failed', err),
    });
  }

  retryJob(jobId: string): void {
    this.http.post<ImportJob>(`${BASE}/retry/${jobId}`, {}).subscribe({
      next: (job) => {
        const startedAt = new Date(job.createdAt).getTime();
        const enriched: ImportJob = { ...job, _startedAt: startedAt, elapsedMs: Date.now() - startedAt };
        this.jobs.update(list => list.map(j => j.jobId === jobId ? enriched : j));
        this.scheduleNextPoll(jobId);
      },
      error: (err) => console.error('[TradexImport] retry failed — session may have expired, please re-upload', err),
    });
  }

  clearCompleted(): void {
    const kept = this.jobs().filter(j => j.status === 'QUEUED' || j.status === 'PROCESSING');
    this.jobs().filter(j => j.status === 'DONE' || j.status === 'ERROR').forEach(j => this.stopPolling(j.jobId));
    this.jobs.set(kept);
  }

  dismissJob(jobId: string): void {
    this.stopPolling(jobId);
    this.jobs.update(list => list.filter(j => j.jobId !== jobId));
  }

  private registerJob(job: ImportJob): void {
    const startedAt = new Date(job.createdAt).getTime();
    const enriched: ImportJob = { ...job, _startedAt: startedAt, elapsedMs: Date.now() - startedAt };
    this.jobs.update(list => [enriched, ...list]);
    // First poll fires quickly so the step indicator catches READING before Lambda moves on
    this.stopPolling(job.jobId);
    const t = setTimeout(() => this.pollAndSchedule(job.jobId), FIRST_POLL_MS);
    this.timers.set(job.jobId, t);
  }

  private restore(): void {
    const key = STORAGE_KEY(this.userEmail());
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const saved: ImportJob[] = JSON.parse(raw);
      if (!Array.isArray(saved) || saved.length === 0) return;

      const now = Date.now();
      const valid = saved.filter(j => now - new Date(j.createdAt).getTime() < JOB_EXPIRY_MS);
      const restored = valid.map(j => {
        const startedAt = new Date(j.createdAt).getTime();
        return { ...j, _startedAt: startedAt, elapsedMs: now - startedAt };
      });

      this.jobs.set(restored);
      restored
        .filter(j => j.status === 'QUEUED' || j.status === 'PROCESSING')
        .forEach(j => this.scheduleNextPoll(j.jobId));
    } catch {
      localStorage.removeItem(STORAGE_KEY(this.userEmail()));
    }
  }

  private scheduleNextPoll(jobId: string): void {
    this.stopPolling(jobId);
    const job = this.jobs().find(j => j.jobId === jobId);
    if (!job || job.status === 'DONE' || job.status === 'ERROR') return;

    const delay = (job.elapsedMs ?? 0) > SLOW_AFTER_MS ? SLOW_POLL_MS : FAST_POLL_MS;
    const t = setTimeout(() => this.pollAndSchedule(jobId), delay);
    this.timers.set(jobId, t);
  }

  private pollAndSchedule(jobId: string): void {
    this.http.get<ImportJob>(`${BASE}/status/${jobId}`, {
      context: new HttpContext().set(SKIP_LOADING, true),
    }).subscribe({
      next: (updated) => {
        const prev = this.jobs().find(j => j.jobId === jobId);
        const startedAt = prev?._startedAt ?? new Date(updated.createdAt).getTime();
        this.jobs.update(list =>
          list.map(j => j.jobId === jobId
            ? { ...updated, _startedAt: startedAt, elapsedMs: Date.now() - startedAt }
            : j
          )
        );
        if (updated.status === 'DONE' || updated.status === 'ERROR') {
          this.stopPolling(jobId);
          if (updated.status === 'DONE') {
            this.notifyDone(updated);
            this.jobCompleted$.next(updated);
          }
        } else {
          this.scheduleNextPoll(jobId);
        }
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 404) {
          // Job gone from BE (server restart) — stop spam, surface as error
          this.stopPolling(jobId);
          this.jobs.update(list =>
            list.map(j => j.jobId === jobId
              ? { ...j, status: 'ERROR' as ImportJobStatus, errorMessage: 'Session expired — please re-upload your files.' }
              : j
            )
          );
        } else {
          this.scheduleNextPoll(jobId);
        }
      },
    });
  }

  private stopPolling(jobId: string): void {
    const t = this.timers.get(jobId);
    if (t != null) clearTimeout(t);
    this.timers.delete(jobId);
  }

  private notifyDone(job: ImportJob): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const body = job.targetCertId
      ? 'Documents processed and merged into your certificate.'
      : 'Your catch certificate is ready.';
    try {
      new Notification('TradeX Import Complete', { body, icon: '/favicon.ico' });
    } catch { /* some browsers block this */ }
  }

  private requestNotificationPermission(): void {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }

  private userEmail(): string {
    return this.auth.userData?.username ?? this.auth.accessToken?.split('.')[1] ?? 'anonymous';
  }
}
